//Imports
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo
} from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
  Container,
  TableView,
  SearchInput,
  TableButtons,
  IconButton,
  MenuComponent,
  useTablePagination,
} from "@gpstrackit/gtc-react-controls";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "Components/Notifications";
import Preferences from "Core/containers/Preferences";

//Actions
import { reset, manageSearchKeyFobs } from "./redux/slice";
import { sendGenericExport, clearRedux, savePreference } from "Redux/actions";
import { openModalCreateEditManageKeyFob } from "./modals/createEditManageKeyFob/redux/slice";
import { openModalDeleteManageKeyFob } from "./modals/deleteManageKeyFob/redux/slice";

//Modals
import DeleteManageKeyFob from "./modals/deleteManageKeyFob";
import CreateEditManageKeyFob from "./modals/createEditManageKeyFob";

//Helpers
import { completeColumns } from "Core/data/Helpers";
import { disableItemSelection } from "../../.globals/utils";
import { HasPermission } from "Core/security"
const KEY_PREFERENCE = "Manage-KeyFobsTab-list";
const INITIAL_SORT = { key: "hex", order: "asc" };

const KeyFobsTab = () => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Language
  const { messages, locale } = useIntl();

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [346])

  //Table refs
  const keyFobTableRef = useRef();

  //Redux
  const dispatch = useDispatch();
  const pagination = useTablePagination({
    dispatch,
    action: manageSearchKeyFobs,
    searchFields: ['hex', 'driver_name'],
    tableRef: keyFobTableRef,
    sendGenericExport,
    exportName: 'keyFobs'
  });
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const { loading, items, total, error } = useSelector(
    (state) => state.manageKeyFobsTabRedux
  );
  const openCreateEditManageKeyFob = useSelector(
    (state) => state.manageCreateEditManageKeyFobRedux.open
  );
  const openDeleteManageKeyFob = useSelector(
    (state) => state.manageDeleteManageKeyFobRedux.open
  );
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);

  //Options
  const menuKeyFobsTabOptions = [messages["edit"], messages["delete"]];


  if (exportResult && exportName === "keyFobs") {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }

  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  }

  //Memos
  const currentColumns = useMemo(() => [
    {
      key: "hex",
      title: messages["manage_keyFob"],
      functionString: 'return row?.hex || ""',
      sort: true,
      show: true,
      export: true,
      exportValue: (row) => row?.hex,
      render: (row) => (
        <Grid container alignItems="center">
          <Grid item>
            <p>{row?.hex || ''}</p>
          </Grid>
          {editablePermissions &&
            <Grid item>
              <MenuComponent
                placement="right-start"
                disabled={loading}
                options={menuKeyFobsTabOptions}
                icon="more_vert"
                className="showInHover"
                onClick={(type, keyFob) => { handleClickMenu(type, keyFob) }}
                value={row}
              />
            </Grid>
          }
        </Grid>
      ),
    },
    {
      key: "driver",
      title: messages["manage_driver"],
      functionString: 'return row?.driver?.title || ""',
      show: true,
      export: true,
      exportValue: (row) => {
        return row?.driver?.title},
      render: (row) => (
        <Grid container alignItems="center">
          <Grid item>
            <p>{row?.driver?.title || ''}</p>
          </Grid>
        </Grid>
      ),
    },
  ], [locale]);

  //Handlers and utils
  const handleClickMenu = (type, keyFob) => {
    if (type === messages["edit"]) {
      dispatch(openModalCreateEditManageKeyFob(keyFob));
      return;
    }
    if (type === messages["delete"]) {
      if (keyFob.id)
        dispatch(openModalDeleteManageKeyFob({ deleteKeyFob: keyFob }));
      return;
    }
  };

  const handleDeleteKeyFobList = useCallback(() => {
    dispatch(openModalDeleteManageKeyFob({ deleteKeyFobList: selectedItems }));
  }, [selectedItems]);

  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences
        })
      );
    },
    [preferences]
  );

  useEffect(() => {
    // Only execute when preferences are loaded and columns are NOT initialized
    if (!loadPreferences && !loadColumns) {
      if (preferences?.columns?.length) {
        const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

        // Reorder columns based on preferences
        const reorderedColumns = preferences.columns
          .map((pref) => {
            const col = currentColumns.find((c) => c.key === pref.key);
            return col ? { ...col, show: pref.show } : null;
          })
          .filter(Boolean);

        // Add new columns that are not in preferences
        const newColumns = currentColumns
          .filter((col) => !preferenceMap.has(col.key))
          .map((col) => ({ ...col, show: true }));

        const finalColumns = [...reorderedColumns, ...newColumns];

        pagination.setColumns(finalColumns);
      } else {
        // If there are no column preferences, use the current columns
        pagination.setColumns(currentColumns);
      }
      
      setLoadColumns(true);
    }
  }, [loadPreferences, loadColumns, preferences?.columns, currentColumns, pagination]);

  useEffect(() => {
    // When the language changes, reset the columns
    if (!loadPreferences && loadColumns) {
      const updatedColumns = currentColumns.map((col) => {
        const existingCol = pagination.columns.find((c) => c.key === col.key);
        return existingCol ? { ...col, show: existingCol.show } : col;
      });
      
      pagination.setColumns(updatedColumns);
      setPreferences((prev) => ({ ...prev, columns: updatedColumns }));
    }
  }, [locale]);

  return (
    <Fragment>
      <Container maxWidth={false}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <>
                    <IconButton
                      tooltip={messages["add"]}
                      placement="bottom"
                      variant="secondary"
                      icon="add_box"
                      size="small"
                      disabled={loading}
                      onClick={() => dispatch(openModalCreateEditManageKeyFob())}
                    />
                    <IconButton
                      tooltip={messages["delete"]}
                      placement="bottom"
                      variant="secondary"
                      icon="delete"
                      size="small"
                      onClick={() => handleDeleteKeyFobList()}
                      style={{ opacity: !selectedItems.length && "0.6" }}
                      isDisabled={!selectedItems.length}
                    />
                  </>
                }
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...pagination}
                  onChangeColumns={(columns) => {
                    const hasVisibleColumn = columns.some(col => col.show);
                    const validatedColumns = hasVisibleColumn 
                      ? columns 
                      : columns.map((col, index) => 
                          index === 0 ? { ...col, show: true } : col
                        );
                    
                    savePreferences([{ key: "columns", value: validatedColumns }]);
                    pagination.setColumns(validatedColumns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_keyFobs_search_by_key_fob_number"]}
              searchText={pagination?.searchText}
              onSearchText={pagination.onSearchChange}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              keyFobTableRef.current?.setSort?.(sort);
              pagination.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loading || loadPreferences}
            topheight={235}
            data={items}
            total={total}
            disableItemSelection={disableItemSelection(preferences)}
            exportOrientation="landscape"
            onSelectedItems={(items) => setSelectedItems(items)}
            {...pagination}
            onChangeColumns={(columns) => {
              const newcurrentColumns = pagination?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              pagination.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              pagination.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>
      {openCreateEditManageKeyFob && (
        <CreateEditManageKeyFob refresh={() => pagination.onSearchChange("")} />
      )}
      {openDeleteManageKeyFob && (
        <DeleteManageKeyFob refresh={() => pagination.onSearchChange("")} />
      )}
    </Fragment>
  );
};

export default KeyFobsTab;
